import { pixelToDxa } from '@/libs/utils/docxUtils'
import {
  AlignmentType,
  PageBreak,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
  BorderStyle,
  HeadingLevel,
  TableLayoutType,
} from 'docx'
import SectionHeader from '../../SectionHeader'
import { tableWidthDxa1150 } from '../../constants'

const Cell = ({ children, text = '', textProps = {}, ...props }) =>
  new TableCell({
    margins: { top: 100, bottom: 200, left: 100, right: 100 },
    verticalAlign: VerticalAlign.CENTER,
    children: [
      new Paragraph({
        children: Array.isArray(children) ? children : [new TextRun({ text, ...textProps })], // Ensure children is an array
        alignment: AlignmentType.CENTER,
      }),
    ],
    ...props,
  })

const HeaderCell = ({ textProps, ...props }) =>
  Cell({
    textProps: { ...textProps, bold: true },
    ...props,
  })

const Chapter4Page = ({ reportData }) => {
  const { phishCampaignReport } = reportData || {}

  const {
    userCount,
    fileCount,
    linkCount,
    openCount,
    textCount,
    openPercent,
    linkPercent,
    filePercent,
    textPercent,
  } = phishCampaignReport || {}

  const formatPercent = value => {
    if (value != null) {
      return Number(value).toFixed(2) // 強制保留兩位小數
    }
    return '0.00' // 無效值回傳 '0.00'
  }

  const formattedReport = {
    userCount,
    openCount,
    openPercent: formatPercent(openPercent),
    linkCount,
    linkPercent: formatPercent(linkPercent),
    fileCount,
    filePercent: formatPercent(filePercent),
    textCount,
    textPercent: formatPercent(textPercent),
  }

  return [
    new Paragraph({ children: [new PageBreak()] }),
    new Paragraph(''),
    SectionHeader({
      text: '第四章 測試分析報告',
      size: 52,
      headingNumber: HeadingLevel.HEADING_1,
    }),
    new Paragraph({
      border: {
        top: {
          color: '#000000',
          style: BorderStyle.SINGLE,
          size: 25, // 控制橫線的粗細
        },
      },
    }),
    SectionHeader({
      text: '4.1 受測者總結果統計',
      size: 48,
      headingNumber: HeadingLevel.HEADING_2,
    }),
    new Paragraph(''),
    new Paragraph({
      style: 'textParagraph',
      children: [
        new TextRun({
          text: '\t',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '依照測試結果分析，列出整體結果統計圖表，依據',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '各類型範本郵件',
          bold: true,
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '檢測結果，蒐集所有檢測數據所統計出的整體人數結果統計圖表。',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
      ],
    }),
    new Paragraph(''),
    new Paragraph({
      style: 'textParagraph',
      children: [
        new TextRun({
          text: '\t',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '依照',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '受測人數',
          bold: true,
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '統計之',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '「瀏覽郵件」、「點閱連結」、「開啟附件」與「輸入資料」',

          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '之人數統計報表：',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
      ],
    }),
    // new Paragraph(''),
    new Paragraph({
      style: 'textParagraph',
      alignment: AlignmentType.RIGHT,
      children: [
        new TextRun({
          text: '單位：人',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
      ],
    }),
    new Table({
      indent: { size: pixelToDxa(-50), type: WidthType.DXA },
      layout: TableLayoutType.FIXED,
      // columnWidths: Array(9).fill(tableWidthDxa1150 / 9), // 自動填充 9 個等寬欄位
      columnWidths: [0.1, 0.1125, 0.1125, 0.1125, 0.1125, 0.1125, 0.1125, 0.1125, 0.1125].map(
        ratio => tableWidthDxa1150 * ratio,
      ),
      width: { size: tableWidthDxa1150, type: WidthType.DXA },
      rows: [
        // 第一行 - 表頭，顯示受測人數以及每個分類的總稱
        new TableRow({
          children: [
            HeaderCell({
              children: [
                new TextRun({
                  text: '受測人數',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
              rowSpan: 2,
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '瀏覽信件',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
              columnSpan: 2,
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '點閱連結',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
              columnSpan: 2,
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '開啟附件',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
              columnSpan: 2,
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '輸入資料',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
              columnSpan: 2,
            }),
          ],
        }),
        // 第二行 - 表頭的第二行，顯示人數和比率欄位
        // new TableRow({
        //   children: [
        //     HeaderCell({ text: '人數', shading: { fill: '#d7e0ed' }, bold: true, font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' }, size: 20 }),
        //     HeaderCell({ text: '比率', shading: { fill: '#d7e0ed' }, bold: true, font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' }, size: 20 }),
        //     HeaderCell({ text: '人數', shading: { fill: '#d7e0ed' }, bold: true, font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' }, size: 20 }),
        //     HeaderCell({ text: '比率', shading: { fill: '#d7e0ed' }, bold: true, font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' }, size: 20 }),
        //     HeaderCell({ text: '人數', shading: { fill: '#d7e0ed' }, bold: true, font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' }, size: 20 }),
        //     HeaderCell({ text: '比率', shading: { fill: '#d7e0ed' }, bold: true, font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' }, size: 20 }),
        //     HeaderCell({ text: '人數', shading: { fill: '#d7e0ed' }, bold: true, font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' }, size: 20 }),
        //     HeaderCell({ text: '比率', shading: { fill: '#d7e0ed' }, bold: true, font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' }, size: 20 }),
        //   ],
        // }),

        new TableRow({
          children: [
            HeaderCell({
              children: [
                new TextRun({
                  text: '人數',
                  bold: true,
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#d7e0ed' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '比率',
                  bold: true,
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#d7e0ed' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '人數',
                  bold: true,
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#d7e0ed' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '比率',
                  bold: true,
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#d7e0ed' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '人數',
                  bold: true,
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#d7e0ed' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '比率',
                  bold: true,
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#d7e0ed' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '人數',
                  bold: true,
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#d7e0ed' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '比率',
                  bold: true,
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#d7e0ed' },
            }),
          ],
        }),

        // 第三行 - 數據行，顯示對應的數據
        // new TableRow({
        //   children: [
        //     Cell({ text: `${formattedReport.userCount || 0}`, shading: { fill: '#e8ecf2' },  size: 20 }), // 受測人數
        //     Cell({ text: `${formattedReport.openCount || 0}`, shading: { fill: '#e8ecf2' },  size: 20 }), // 瀏覽信件人數
        //     Cell({ text: `${formattedReport.openPercent || 0}%`, shading: { fill: '#e8ecf2' },  size: 20 }), // 瀏覽信件比率
        //     Cell({ text: `${formattedReport.linkCount || 0}`, shading: { fill: '#e8ecf2' },  size: 20 }), // 點閱連結人數
        //     Cell({ text: `${formattedReport.linkPercent || 0}%`, shading: { fill: '#e8ecf2' },  size: 20 }), // 點閱連結比率
        //     Cell({ text: `${formattedReport.fileCount || 0}`, shading: { fill: '#e8ecf2' },  size: 20 }), // 開啟附件人數
        //     Cell({ text: `${formattedReport.filePercent || 0}%`, shading: { fill: '#e8ecf2' },  size: 20 }), // 開啟附件比率
        //     Cell({ text: `${formattedReport.textCount || 0}`, shading: { fill: '#e8ecf2' },  size: 20 }), // 輸入資料人數
        //     Cell({ text: `${formattedReport.textPercent || 0}%`, shading: { fill: '#e8ecf2' },  size: 20 }), // 輸入資料比率
        //   ],
        // }),

        new TableRow({
          children: [
            Cell({
              children: [
                new TextRun({
                  text: `${formattedReport.userCount || 0}`,

                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#e8ecf2' },
            }),
            Cell({
              children: [
                new TextRun({
                  text: `${formattedReport.openCount || 0}`,

                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#e8ecf2' },
            }),
            Cell({
              children: [
                new TextRun({
                  text: `${formattedReport.openPercent || 0}%`,

                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#e8ecf2' },
            }),
            Cell({
              children: [
                new TextRun({
                  text: `${formattedReport.linkCount || 0}`,

                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#e8ecf2' },
            }),
            Cell({
              children: [
                new TextRun({
                  text: `${formattedReport.linkPercent || 0}%`,

                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#e8ecf2' },
            }),
            Cell({
              children: [
                new TextRun({
                  text: `${formattedReport.fileCount || 0}`,

                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#e8ecf2' },
            }),
            Cell({
              children: [
                new TextRun({
                  text: `${formattedReport.filePercent || 0}%`,

                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#e8ecf2' },
            }),
            Cell({
              children: [
                new TextRun({
                  text: `${formattedReport.textCount || 0}`,

                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#e8ecf2' },
            }),
            Cell({
              children: [
                new TextRun({
                  text: `${formattedReport.textPercent || 0}%`,

                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#e8ecf2' },
            }),
          ],
        }),
      ],
    }),
    new Paragraph(''),
    // new Paragraph({
    //   // style: 'textParagraph',
    //   alignment: AlignmentType.RIGHT,
    //   children: [new TextRun({ text: '單位：人', font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' }, size: 24 })],
    // }),
  ]
}

export default Chapter4Page
